import { useState, useEffect, useCallback } from 'react';
import { proverbApiService } from '../services/proverbApiService';
import { useActivityService } from '../services/activityService';
import { useAuth } from '../contexts/AuthContext';

const useProverbs = () => {
  const { addActivity, TYPES } = useActivityService();
  const { user, token } = useAuth();
  const [proverbs, setProverbs] = useState([]);
  const [filteredProverbs, setFilteredProverbs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [languageFilter, setLanguageFilter] = useState('');
  const [stats, setStats] = useState({
    total: 0,
    chinese: 0,
    english: 0,
    french: 0,
    arabic: 0
  });

  // Calculate stats
  const updateStats = (proverbsData) => {
    const newStats = {
      total: proverbsData.length,
      chinese: proverbsData.filter(p => p.chinese_proverb && p.chinese_proverb.trim()).length,
      english: proverbsData.filter(p => p.english_translation && p.english_translation.trim()).length,
      french: proverbsData.filter(p => p.french_translation && p.french_translation.trim()).length,
      arabic: proverbsData.filter(p => p.arabic_translation && p.arabic_translation.trim()).length
    };
    setStats(newStats);
  };

  // Load proverbs from backend API
  const loadProverbs = async () => {
    setLoading(true);
    try {
      // Check if user is authenticated
      if (!user || !token) {
        console.warn('⚠️ No authenticated user found. Please log in to access proverbs.');
        setProverbs([]);
        setFilteredProverbs([]);
        updateStats([]);
        return;
      }

      console.log('📚 Loading proverbs from backend API...');
      
      const response = await proverbApiService.getAllProverbs();
      const data = response.proverbs || response;
      
      if (data && data.length > 0) {
        // Sort by proverbId first (if available), then by creation date - newest first
        const sortedData = data.sort((a, b) => {
          // If both have proverbId, sort by proverbId (descending)
          if (a.proverbId && b.proverbId) {
            return b.proverbId - a.proverbId;
          }
          
          // If only one has proverbId, put it first
          if (a.proverbId && !b.proverbId) return -1;
          if (!a.proverbId && b.proverbId) return 1;
          
          // If neither has proverbId, sort by creation date
          const dateA = new Date(a.created_at || a.createdAt || '1970-01-01');
          const dateB = new Date(b.created_at || b.createdAt || '1970-01-01');
          return dateB - dateA;
        });
        
        console.log(`✅ Successfully loaded ${sortedData.length} proverbs (sorted by ID/date, newest first)`);
        setProverbs(sortedData);
        setFilteredProverbs(sortedData);
        updateStats(sortedData);
      } else {
        console.log('📝 No proverbs found in backend');
        setProverbs([]);
        setFilteredProverbs([]);
        updateStats([]);
      }
    } catch (error) {
      console.error('❌ Error loading proverbs from backend:', error);
      
      if (error.message.includes('session has expired') || 
          error.message.includes('jwt expired') ||
          error.message.includes('Authentication required')) {
        console.warn('🔐 Authentication token expired - user will be logged out automatically');
        // The axios interceptor in AuthContext will handle logout
        setProverbs([]);
        setFilteredProverbs([]);
        updateStats([]);
        return;
      }
      
      if (error.message.includes('Permission denied')) {
        console.warn('🚫 Permission denied - user lacks access');
      }
      
      // For other errors, show empty state
      console.log('� Using empty state due to API error:', error.message);
      setProverbs([]);
      setFilteredProverbs([]);
      updateStats([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter proverbs
  const filterProverbs = useCallback(() => {
    let filtered = proverbs.filter(proverb => {
      if (!searchTerm) {
        return true;
      }

      const searchTermNum = parseInt(searchTerm);
      const proverbId = parseInt(proverb.id);
      if (!isNaN(searchTermNum) && !isNaN(proverbId) && proverbId === searchTermNum) {
        return true;
      }

      if (!isNaN(searchTermNum) && searchTerm.trim() === searchTermNum.toString()) {
        return false;
      }

      const matchesText = 
        proverb.chinese_proverb?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        proverb.pinyin?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        proverb.english_translation?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        proverb.english_explanation?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        proverb.french_translation?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        proverb.arabic_translation?.toLowerCase().includes(searchTerm.toLowerCase());

      return matchesText;
    });

    filtered = filtered.filter(proverb => {
      if (!languageFilter) return true;
      
      const matchesLanguage = 
        (languageFilter === 'chinese' && proverb.chinese_proverb) ||
        (languageFilter === 'english' && proverb.english_translation) ||
        (languageFilter === 'french' && proverb.french_translation) ||
        (languageFilter === 'arabic' && proverb.arabic_translation);

      return matchesLanguage;
    });

    filtered = filtered.sort((a, b) => {
      const aId = parseInt(a.id) || 0;
      const bId = parseInt(b.id) || 0;
      return bId - aId;
    });

    setFilteredProverbs(filtered);
  }, [proverbs, searchTerm, languageFilter]);

  // Create proverb via backend API
  const createProverb = async (formData) => {
    setLoading(true);
    try {
      // Check authentication before attempting to create
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication required. Please log in to create proverbs.');
      }

      console.log('📝 Creating new proverb via backend API...');
      
      const newProverb = await proverbApiService.createProverb(formData);
      
      // Add the new proverb at the beginning of the list (newest first)
      const updatedProverbs = [newProverb, ...proverbs];
      setProverbs(updatedProverbs);
      updateStats(updatedProverbs);
      
      // Log proverb creation activity
      const chineseText = formData.chinese_proverb || 'New proverb';
      await addActivity(TYPES.PROVERB_ADD, `New proverb added via API: "${chineseText}"`);
      
      console.log('✅ Proverb created and added to local state:', newProverb.id);
      
      return newProverb;
    } catch (error) {
      console.error('❌ Error creating proverb via backend API:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Update proverb via backend API
  const updateProverb = async (id, formData) => {
    setLoading(true);
    try {
      console.log('📝 Updating proverb via backend API:', id);
      
      const updatedProverb = await proverbApiService.updateProverb(id, formData);
      const updatedProverbs = proverbs.map(p => p.id === updatedProverb.id ? updatedProverb : p);
      setProverbs(updatedProverbs);
      updateStats(updatedProverbs);
      
      // Log proverb update activity
      const chineseText = formData.chinese_proverb || updatedProverb.chinese_proverb || 'Proverb';
      await addActivity(TYPES.PROVERB_EDIT, `Proverb updated via API: "${chineseText}"`);
      
      console.log('✅ Proverb updated successfully:', id);
      
      return updatedProverb;
    } catch (error) {
      console.error('❌ Error updating proverb via backend API:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Delete proverb via backend API
  const deleteProverb = async (id) => {
    setLoading(true);
    try {
      // Get the proverb before deleting for activity log
      const proverbToDelete = proverbs.find(p => p.id === id);
      
      console.log('🗑️ Deleting proverb via backend API:', id);
      
      await proverbApiService.deleteProverb(id);
      const newProverbs = proverbs.filter(p => p.id !== id);
      setProverbs(newProverbs);
      setFilteredProverbs(newProverbs);
      updateStats(newProverbs);
      
      // Log proverb deletion activity
      const chineseText = proverbToDelete?.chinese_proverb || 'Proverb';
      await addActivity(TYPES.PROVERB_DELETE, `Proverb deleted via API: "${chineseText}"`);
      
      console.log('✅ Proverb deleted successfully:', id);
      
    } catch (error) {
      console.error('❌ Error deleting proverb via backend API:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProverbs();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    filterProverbs();
  }, [filterProverbs]);

  return {
    proverbs,
    filteredProverbs,
    loading,
    searchTerm,
    setSearchTerm,
    languageFilter,
    setLanguageFilter,
    stats,
    loadProverbs,
    createProverb,
    updateProverb,
    deleteProverb
  };
};

export default useProverbs;
